// src/pages/fleet/FleetLoginPage.tsx — Fleet portal login (blue theme).
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { login } from '../../services/api';
import { css } from '../../utils/css';
import type { ApiErrorBody } from '../../types';
import axios from 'axios';
import { fleetHome } from './permissions';

export default function FleetLoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await login(email, password);
      const home = fleetHome(res.data.user);
      if (!home) { setError('This account has no Fleet access. Ask an administrator to assign a role.'); return; }
      loginUser(res.data.token, res.data.user);
      navigate(home);
    } catch (err) {
      setError((axios.isAxiosError<ApiErrorBody>(err) ? err.response?.data?.error : undefined) || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes fleetKenBurns { 0% { transform: scale(1.02) translate(0,0); } 100% { transform: scale(1.10) translate(-2.5%, -2%); } }`}</style>
      <div style={S.bgImage} />
      <div style={S.bgOverlay} />
      <div style={S.glowA} /><div style={S.glowB} />
      <div style={S.card}>
        <div style={S.logoRow}>
          <div style={S.logoIcon}><img src="/logo-mark.svg" alt="" width={38} height={38} style={{ display: 'block' }} /></div>
          <div>
            <div style={S.logoName}>Fertilizer Tracking Fleet</div>
            <div style={S.logoSub}>GPS Truck Tracking</div>
          </div>
        </div>

        <h1 style={S.title}>Fleet Control Login</h1>
        <p style={S.subtitle}>Real-time fleet monitoring & trip management</p>

        <form onSubmit={handleSubmit}>
          <div style={S.field}>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="admin@fertilizertracking.gov.in" required />
          </div>
          <div style={S.field}>
            <label style={S.label}>Password</label>
            <div style={{ position: 'relative' }}>
              <input style={S.input} type={showPass ? 'text' : 'password'} value={password}
                onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              <span onClick={() => setShowPass(s => !s)} style={S.eye}>{showPass ? '🙈' : '👁️'}</span>
            </div>
          </div>

          {error && <div style={S.error}>{error}</div>}

          <button type="submit" style={{ ...S.btn, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? 'Signing in…' : 'Open Fleet Portal →'}
          </button>
        </form>

        {/* The demo-credentials box was removed on 2026-09-28: this page is now
            served publicly, and a live sign-in screen must not print a working
            username and password. */}

        <Link to="/" style={S.back}>← All Services</Link>
      </div>
    </div>
  );
}

const NAVY = '#0A3A7A', ACCENT = '#1560BD';
const S = css({
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    background: NAVY, position: 'relative', overflow: 'hidden', fontFamily: "'Inter', sans-serif" },
  bgImage: { position: 'absolute', inset: '-7%', backgroundImage: "url('/fleet-login-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center',
    animation: 'fleetKenBurns 30s ease-in-out infinite alternate', willChange: 'transform' },
  bgOverlay: { position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,58,122,0.42), rgba(6,29,67,0.52))' },
  glowA: { position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: 'rgba(21,96,189,0.35)', filter: 'blur(90px)', top: -80, right: -60 },
  glowB: { position: 'absolute', width: 340, height: 340, borderRadius: '50%', background: 'rgba(46,160,255,0.22)', filter: 'blur(90px)', bottom: -80, left: -60 },
  card: { width: '100%', maxWidth: 420, background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(14px)',
    border: '1px solid rgba(255,255,255,0.14)', borderRadius: 20, padding: '32px 28px', position: 'relative', zIndex: 1, boxShadow: '0 24px 70px rgba(0,0,0,0.45)' },
  logoRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 },
  // Light plate, not the accent gradient: this card sits on a dark photo and
  // the mark's dark green would disappear into it.
  logoIcon: { width: 46, height: 46, borderRadius: 12, background: '#FDFCF5', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' },
  logoName: { color: '#fff', fontSize: 17, fontWeight: 700 },
  logoSub: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  title: { color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: '0 0 22px' },
  field: { marginBottom: 16 },
  label: { display: 'block', color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 600, marginBottom: 6 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, outline: 'none' },
  eye: { position: 'absolute', right: 12, top: 11, cursor: 'pointer', fontSize: 16 },
  error: { background: 'rgba(220,53,69,0.18)', border: '1px solid rgba(220,53,69,0.4)', color: '#ffd7db', fontSize: 12.5, padding: '9px 12px', borderRadius: 8, marginBottom: 14 },
  btn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', cursor: 'pointer',
    background: `linear-gradient(135deg, ${ACCENT}, #2EA0FF)`, color: '#fff', fontSize: 15, fontWeight: 700, boxShadow: '0 8px 24px rgba(21,96,189,0.45)' },
  back: { display: 'block', textAlign: 'center', marginTop: 18, color: 'rgba(255,255,255,0.6)', fontSize: 13, textDecoration: 'none' },
});
